import { useState } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useNotify, useRedirect } from 'react-admin';
import { SectionCard } from '../shared/Panel';
import { PreOrderForm } from './PreOrderForm';
import {
  createPreOrder,
  toErrorMessage,
  type PreOrderInput,
} from './preOrderApi';

/**
 * Wprowadzenie prezlecenia z ręki.
 *
 * ## Dlaczego ten ekran w ogóle jest
 *
 * W repo **nic nie tworzyło prezleceń ani zleceń** — ani seed, ani kontroler,
 * ani importer z TMS. Ekstrakcja z maila (W3) jest odłożona, a skrzynki
 * niedostępne, więc to jedyna droga wejścia zlecenia do systemu — i warunek,
 * żeby zapis do VIA (W10) dał się w ogóle wypróbować.
 *
 * ## Zapis nie wymaga kompletu
 *
 * Spedytor coś wie od razu, resztę dopytuje. Braki policzy serwer i pokaże
 * je na liście oraz na ekranie prezlecenia; komplet jest wymagany dopiero
 * przy akceptacji.
 */
export const PreOrderCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [input, setInput] = useState<PreOrderInput>({});
  const [busy, setBusy] = useState(false);

  const save = () =>
    void (async () => {
      setBusy(true);
      try {
        const p = await createPreOrder(input);
        notify('Prezlecenie wprowadzone', { type: 'success' });
        redirect('show', 'pre-orders', p.id);
      } catch (e) {
        notify(toErrorMessage(e), { type: 'error' });
      } finally {
        setBusy(false);
      }
    })();

  return (
    <Box sx={{ p: 2, maxWidth: 1100 }}>
      <SectionCard title="Nowe prezlecenie">
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1.5 }}
        >
          Wpisz, co wiadomo. Pola, których jeszcze nie znasz, zostaw puste —
          wpisana wartość zastępcza wygląda później jak ustalenie z klientem.
          Komplet jest potrzebny dopiero do akceptacji.
        </Typography>

        <PreOrderForm
          values={input}
          locked={busy}
          onChange={(field, v) => setInput((d) => ({ ...d, [field]: v }))}
          // Kontrahent z kartoteki (fala B, 21.10.2026) — pole pokazuje się
          // tylko z przełącznikiem `preOrderContractor`.
          onContractorChange={(c) => setInput((d) => ({ ...d, contractor: c, contractorId: c?.id ?? null }))}
        />

        <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={busy}
            onClick={save}
            // Kontrakt `data-testid` paczki (S-22.10-02): „utwórz prezlecenie” — u nas ręcznie, nie ze sprawy w skrzynce.
            data-testid="cs-skrzynka-utworz-prezlecenie"
          >
            Zapisz prezlecenie
          </Button>
          {busy && <CircularProgress size={20} />}
        </Stack>
      </SectionCard>
    </Box>
  );
};
